import { Flex } from 'antd';
import { useLiveQuery } from 'dexie-react-hooks';
import { useTranslation } from 'translations/hooks';

import { Header } from 'business/quality-control/pages/record-segment-defect/components/right-panel/ring-details/header';
import { useBoardContext } from 'business/quality-control/pages/record-segment-defect/context/board-context';
import { db, Ring } from 'technical/db';

import { DefectForm } from './defect-form';
import { DetailsHeader } from './details-header';
import { RepairForm } from './repair-form';

export const DefectDetail = ({
  defectReportId,
  ring,
}: {
  defectReportId: string;
  ring: Ring;
}) => {
  const { t } = useTranslation();

  const { stopDrawing, setDefectId } = useBoardContext();

  const queryResult = useLiveQuery(async () => {
    const [defectReport, repairReport] = await Promise.all([
      db.defectReports.get(defectReportId),
      db.repairReports
        .where('defectReportId')
        .equals(defectReportId)
        .and((repair) => repair.deletedAt === null)
        .first(), // we only have 1 repair report by defect
    ]);

    return {
      defectReport: defectReport ?? null,
      repairReport: repairReport ?? null,
    };
  }, [defectReportId]);

  if (queryResult === undefined) {
    return null;
  }

  const { defectReport, repairReport } = queryResult;

  const resetCurrentDefect = () => {
    setDefectId(null);
  };

  const handleCloseItemForm = () => {
    stopDrawing();
    resetCurrentDefect();
  };

  const displayRepairForm =
    defectReport?.submittedAt != null && defectReport?.toRepair;

  return (
    <>
      <Header
        title={t(
          'qualityControl.recordSegmentDefect.rightPanel.ringDetails.defectDetail.title',
        )}
        resetCurrentItem={() => handleCloseItemForm()}
      />

      <Flex gap={24} vertical>
        <DetailsHeader
          ringNumber={ring.ringNumber}
          ringKey={ring.keyPosition}
          segmentType={defectReport?.segmentType ?? null}
          serialNumber={defectReport?.serialNumber ?? null}
        />

        <DefectForm
          defectReportId={defectReportId}
          defectReport={defectReport}
          ringId={ring.id}
          isRepairEditable={displayRepairForm}
          repairReport={repairReport}
          onDelete={() => resetCurrentDefect()}
          closeDefect={() => resetCurrentDefect()}
        />

        {displayRepairForm ? (
          <RepairForm
            defectReportId={defectReportId}
            repairReportId={repairReport?.id ?? crypto.randomUUID()}
            repairReport={repairReport}
            closeDefect={() => resetCurrentDefect()}
            isRingCompliant={ring.compliantDate != null}
          />
        ) : null}
      </Flex>
    </>
  );
};
